import * as yup from 'yup';
import { Features } from '@zignaly-open/ps2-definitions';
import { checkDecimals } from '@zignaly-open/ui';
import { MIN_USD_INVEST_AMOUNT_FIREBLOCKS } from './constants';

export const CommunicationConfigValidation = yup
  .object({
    supportUrl: yup.string().url('config:socials.validation-url'),
    social: yup.object({
      twitter: yup.string().url('config:socials.validation-url'),
      telegram: yup.string().url('config:socials.validation-url'),
      instagram: yup.string().url('config:socials.validation-url'),
      discord: yup.string().url('config:socials.validation-url'),
      youtube: yup.string().url('config:socials.validation-url'),
      medium: yup.string().url('config:socials.validation-url'),
      linkedin: yup.string().url('config:socials.validation-url'),
    }),
  })
  .required();

function checkMinInvestmentIfEnabled(val: number) {
  const isMinInvestmentOn =
    this.from[1]?.value?.settings?.[Features.MinInvestment];
  return !isMinInvestmentOn || val >= 0;
}

function checkMinFireblocksInvestmentIfEnabled(val: number) {
  return val >= MIN_USD_INVEST_AMOUNT_FIREBLOCKS;
}

function checkMinScoreIfEnabled(val: number) {
  const isScoreOn = this.parent?.settings?.[Features.ZScore];
  return !isScoreOn || (val >= 0 && val <= 100);
}

function checkServiceCardsIfEnabled() {
  const isScoreOn = this.parent?.settings?.[Features.ZScore];
  const val = this.parent?.settings?.marketplaceCards;
  return !isScoreOn || (val >= 0 && val <= 9);
}

function checkIntegerServiceCards() {
  const val = this.parent?.settings?.marketplaceCards;
  return checkDecimals(val, 0) && /^[\d]+$/.test(val);
}

export const SettingsConfigValidation = yup
  .object({
    zScoreName: yup
      .string()
      .test('range', 'config:settings.4-letters-max', (v) => v.length <= 4),
    marketplaceMinScore: yup
      .number()
      .typeError('config:settings.validation-number')
      .test(
        'range',
        'config:settings.validation-number-0-100',
        checkMinScoreIfEnabled,
      ),
    ['settings.marketplaceCards']: yup
      .number()
      .typeError('config:settings.validation-number')
      .test(
        'range',
        'config:settings.validation-cards-0-9',
        checkServiceCardsIfEnabled,
      )
      .test(
        'integer',
        'config:settings.must-be-integer',
        checkIntegerServiceCards,
      ),
    minInvestment: yup.object({
      BTC: yup
        .number()
        .typeError('config:settings.validation-number')
        .test(
          'range',
          'config:settings.validation-number-gte-0',
          checkMinInvestmentIfEnabled,
        )
        // have you heard about the horseshoe theory?
        // Yes we could do it with 1 validation message but calling translate here is problematic
        // passing 6 or 8 when rendering the translation error seems like a hassle
        // so we better just have 2 messages lol
        // and no I am not retarded
        .test('int', 'config:settings.max-decimals-8', (val) =>
          checkDecimals(val, 8),
        ),
      ETH: yup
        .number()
        .typeError('config:settings.validation-number')
        .test(
          'range',
          'config:settings.validation-number-gte-0',
          checkMinInvestmentIfEnabled,
        )
        .test('int', 'config:settings.max-decimals-8', (val) =>
          checkDecimals(val, 8),
        ),
      USDT: yup
        .number()
        .typeError('config:settings.validation-number')
        .test(
          'range',
          'config:settings.validation-number-gte-0',
          checkMinInvestmentIfEnabled,
        )
        .test(
          'range2',
          'config:settings.validation-number-min-fireblocks',
          checkMinFireblocksInvestmentIfEnabled,
        )
        .test('int', 'config:settings.max-decimals-6', (val) =>
          checkDecimals(val, 6),
        ),
      BNB: yup
        .number()
        .typeError('config:settings.validation-number')
        .test(
          'range',
          'config:settings.validation-number-gte-0',
          checkMinInvestmentIfEnabled,
        )
        .test('int', 'config:settings.max-decimals-8', (val) =>
          checkDecimals(val, 8),
        ),
    }),
  })
  .required();

const validateImage = (value: File | string | null) => {
  if (value instanceof File) {
    return ['image/png', 'image/svg', 'image/jpg', 'image/jpeg'].includes(
      value.type,
    );
  }
  return true;
};

export const ProfileConfigValidation = yup
  .object({
    name: yup.string().required('common:required'),
    logo: yup
      .mixed()
      .test('required', 'common:required', (v) => !!v)
      .test('mime-type', 'common:error.invalid-type', validateImage),
    image: yup
      .mixed()
      .test('required', 'common:required', (v) => !!v)
      .test('mime-type', 'common:error.invalid-type', validateImage),
    favicon: yup
      .mixed()
      .test('required', 'common:required', (v) => !!v)
      .test('mime-type', 'common:error.invalid-type', validateImage),
    title: yup
      .string()
      .required('common:required')
      .test('max', 'config:should-be-under-x-chars', (v) => v.length < 100),
    description: yup
      .string()
      .required('common:required')
      .test('max', 'config:should-be-under-x-chars', (v) => v.length < 500),

    privacyPolicy: yup.string().url('config:socials.validation-url'),
    tos: yup.string().url('config:socials.validation-url'),

    tools: yup.object({
      google_tag_manager: yup
        .string()
        .test(
          'format',
          'config:gtm-pattern',
          (v) => !v || /^GTM-[A-Z\d]{6,10}$/.test(v),
        ),
      ga_measurement_id: yup
        .string()
        .test(
          'format',
          'config:gtm-pattern-measurement',
          (v) => !v || /^G-[A-Z\d]{6,12}$/.test(v),
        ),
    }),
  })
  .required();
